import { useEffect, useState, useCallback } from "react";
import { Globe, Loader2, Save, Plus, Trash2, ExternalLink, Search, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

export default function AdminSeo() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/seo/admin/config").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/seo/sitemap-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setSD = (k, v) => setCfg((c) => ({ ...c, structured_data: { ...c.structured_data, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        site_name: cfg.site_name, default_title: cfg.default_title, default_description: cfg.default_description,
        default_og_image: cfg.default_og_image, twitter_handle: cfg.twitter_handle, robots_directives: cfg.robots_directives,
        structured_data: cfg.structured_data, static_paths: cfg.static_paths, overrides: cfg.overrides,
      };
      const r = await api.put("/seo/admin/config", payload);
      setCfg(r.data); toast.success("SEO settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const fetchPreview = async () => {
    const p = previewId.trim();
    if (!p) return;
    if (p.startsWith("/")) {
      const ov = (cfg.overrides || []).find((o) => o.path === p) || {};
      setPreview({ kind: "page", path: p, title: ov.title || cfg.default_title, description: ov.description || cfg.default_description, og_image: ov.og_image || cfg.default_og_image });
      return;
    }
    setPreviewBusy(true);
    try { const r = await api.get(`/seo/product/${p}`); setPreview({ ...r.data, kind: "product" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Product not found"); setPreview(null); }
    finally { setPreviewBusy(false); }
  };
  const aiSuggest = async () => {
    const p = previewId.trim();
    if (!p || p.startsWith("/")) { toast.error("Enter a product ID or slug — AI copy is for products"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/seo/admin/suggest", { product_id: p });
      setPreview((pv) => ({ ...(pv || { kind: "product" }), title: r.data.title, description: r.data.description, suggested: true }));
      toast.success("AI draft ready — review it in the preview");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setAiBusy(false); }
  };
  const saveOverride = async () => {
    if (!preview) return;
    const p = previewId.trim();
    const path = p.startsWith("/") ? p : `/product/${p}`;
    const overrides = [...(cfg.overrides || [])];
    const entry = { path, title: preview.title, description: preview.description, og_image: preview.og_image || "" };
    const idx = overrides.findIndex((o) => o.path === path);
    if (idx >= 0) overrides[idx] = { ...overrides[idx], ...entry }; else overrides.push(entry);
    try { const r = await api.put("/seo/admin/config", { overrides }); setCfg(r.data); toast.success(`Saved as override for ${path}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addOverride = () => set({ overrides: [...(cfg.overrides || []), { path: "/", title: "", description: "", noindex: false }] });
  const setOverride = (i, patch) => set({ overrides: cfg.overrides.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) });
  const delOverride = (i) => set({ overrides: cfg.overrides.filter((_, idx) => idx !== i) });

  if (!cfg) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-300";

  return (
    <div className="space-y-6" data-testid="admin-seo">
      <PageHeader icon={Globe} title="SEO Manager" subtitle="Control page titles, meta, Open Graph, robots, structured data and the sitemap." />

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-slate-800">Global defaults</p>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-xs font-medium text-slate-500">Site name
            <input value={cfg.site_name || ""} onChange={(e) => set({ site_name: e.target.value })} data-testid="seo-site-name" className={inputCls} /></label>
          <label className="text-xs font-medium text-slate-500">Twitter handle
            <input value={cfg.twitter_handle || ""} onChange={(e) => set({ twitter_handle: e.target.value })} placeholder="@suffleonline" data-testid="seo-twitter" className={inputCls} /></label>
          <label className="text-xs font-medium text-slate-500 md:col-span-2">Default title
            <input value={cfg.default_title || ""} onChange={(e) => set({ default_title: e.target.value })} data-testid="seo-default-title" className={inputCls} /></label>
          <label className="text-xs font-medium text-slate-500 md:col-span-2">Default meta description
            <textarea value={cfg.default_description || ""} onChange={(e) => set({ default_description: e.target.value })} rows={2} data-testid="seo-default-desc" className={inputCls} /></label>
          <label className="text-xs font-medium text-slate-500 md:col-span-2">Default OG / share image URL
            <input value={cfg.default_og_image || ""} onChange={(e) => set({ default_og_image: e.target.value })} placeholder="https://…/og.png" data-testid="seo-og-image" className={inputCls} /></label>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-slate-800">Structured data (JSON-LD)</p>
        <div className="flex flex-wrap gap-4">
          {["organization", "website", "product", "breadcrumb"].map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm capitalize text-slate-600">
              <input type="checkbox" checked={!!cfg.structured_data?.[k]} onChange={(e) => setSD(k, e.target.checked)} data-testid={`seo-sd-${k}`} /> {k}
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-800">Per-page overrides</p>
          <button onClick={addOverride} data-testid="seo-add-override" className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Add path</button>
        </div>
        {(cfg.overrides || []).length === 0 ? <p className="text-xs text-slate-400">No overrides — every route uses the defaults above.</p> : (
          <div className="space-y-2">
            {cfg.overrides.map((o, i) => (
              <div key={i} className="grid items-center gap-2 rounded-lg border border-slate-100 p-2 md:grid-cols-[1fr_1fr_1.5fr_auto_auto]" data-testid={`seo-override-${i}`}>
                <input value={o.path} onChange={(e) => setOverride(i, { path: e.target.value })} placeholder="/shop" className={inputCls} data-testid={`seo-override-path-${i}`} />
                <input value={o.title || ""} onChange={(e) => setOverride(i, { title: e.target.value })} placeholder="Title" className={inputCls} />
                <input value={o.description || ""} onChange={(e) => setOverride(i, { description: e.target.value })} placeholder="Meta description" className={inputCls} />
                <label className="flex items-center gap-1 text-xs text-slate-500"><input type="checkbox" checked={!!o.noindex} onChange={(e) => setOverride(i, { noindex: e.target.checked })} data-testid={`seo-override-noindex-${i}`} /> noindex</label>
                <button onClick={() => delOverride(i)} data-testid={`seo-override-del-${i}`} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="mb-2 text-sm font-semibold text-slate-800">robots.txt</p>
          <textarea value={cfg.robots_directives || ""} onChange={(e) => set({ robots_directives: e.target.value })} rows={6} data-testid="seo-robots" className={`${inputCls} font-mono text-xs`} />
          <label className="mt-3 block text-xs font-medium text-slate-500">Static paths in sitemap (comma-separated)
            <input value={(cfg.static_paths || []).join(", ")} onChange={(e) => set({ static_paths: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="seo-static-paths" className={inputCls} /></label>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="mb-2 text-sm font-semibold text-slate-800">Sitemap</p>
          {stats && (
            <div className="space-y-1 text-sm text-slate-600" data-testid="seo-sitemap-stats">
              <p>Static pages: <b>{stats.static}</b></p>
              <p>Published products: <b>{stats.products}</b></p>
              <p>Legal pages: <b>{stats.legal}</b></p>
              <a href={stats.sitemap_url} target="_blank" rel="noreferrer noopener noreferrer" data-testid="seo-view-sitemap" className="mt-2 inline-flex items-center gap-1 text-blue-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View sitemap.xml</a><br />
              <a href={stats.robots_url} target="_blank" rel="noreferrer noopener noreferrer" data-testid="seo-view-robots" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View robots.txt</a>
            </div>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">On the deployed domain, alias <code>/sitemap.xml</code> and <code>/robots.txt</code> to these endpoints and submit the sitemap in Google Search Console.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="seo-preview">
        <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Search className="h-4 w-4" /> Search &amp; social preview</p>
        <p className="mb-3 text-xs text-slate-400">Enter a product ID/slug, or a path like <code>/shop</code>, to see how it appears on Google and social. Use AI suggest to draft optimised copy for a product.</p>
        <div className="flex gap-2">
          <input value={previewId} onChange={(e) => setPreviewId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchPreview()} placeholder="Product ID/slug, or a path like /shop" data-testid="seo-preview-input" className={inputCls} />
          <button onClick={fetchPreview} disabled={previewBusy} data-testid="seo-preview-btn" className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {previewBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Preview
          </button>
          <button onClick={aiSuggest} disabled={aiBusy || previewId.trim().startsWith("/")} data-testid="seo-ai-suggest" className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50">
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI suggest
          </button>
          <button onClick={saveOverride} disabled={!preview} data-testid="seo-save-override" className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
            <Save className="h-4 w-4" /> Save as override
          </button>
        </div>
        {preview && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-4" data-testid="seo-preview-serp">
              <p className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">Google result {preview.suggested && <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-blue-700">AI draft</span>}</p>
              <p className="truncate text-xs text-emerald-700">{(cfg.base_url || "").replace(/^https?:\/\//, "")}{preview.kind === "page" ? preview.path : ` › product › ${previewId}`}</p>
              <p className="truncate text-lg leading-tight text-blue-700">{preview.title}</p>
              <p className="line-clamp-2 text-sm text-slate-600">{preview.description}</p>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="seo-preview-og">
              {preview.og_image ? <img src={preview.og_image} alt="" className="h-40 w-full object-cover" /> : <div className="flex h-40 items-center justify-center bg-slate-100 text-xs text-slate-400">No share image set</div>}
              <div className="p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">{(cfg.base_url || "").replace(/^https?:\/\//, "")}</p>
                <p className="truncate text-sm font-semibold text-slate-800">{preview.title}</p>
                <p className="line-clamp-2 text-xs text-slate-500">{preview.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="seo-save" className="flex items-center gap-2 rounded-full brand-gradient-bg px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save SEO settings
        </button>
      </div>
    </div>
  );
}
